<template>
  <div
    class="music-item"
    :class="[`music-item--${variant}`, { 'music-item--selected': selected }]"
    @click="$emit('click')"
  >
    <!-- 不使用音乐图标 -->
    <div v-if="variant === 'no-music'" class="music-item__icon music-item__icon--no-music">
      <svg viewBox="0 0 24 24" fill="none" class="no-music-svg">
        <circle cx="12" cy="12" r="10" stroke="#160211" stroke-width="1.4" />
        <line
          x1="4"
          y1="4"
          x2="20"
          y2="20"
          stroke="#160211"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
    </div>

    <!-- 上传图标 -->
    <div v-else-if="variant === 'upload'" class="music-item__icon music-item__icon--upload">
      <SvgIcon name="editor-upload" :size="24" />
    </div>

    <!-- 音轨图标（可点击试听） -->
    <div
      v-else
      class="music-item__icon music-item__icon--track"
      :class="{ 'music-item__icon--playing': isPlaying }"
      @click="handleIconClick"
    >
      <SvgIcon v-if="isPlaying" name="book-pause" :size="20" color="#ffffff" />
      <SvgIcon v-else name="editor-music" :size="20" color="#6033E1" />
    </div>

    <span class="music-item__name">{{ name }}</span>
    <span
      v-if="variant === 'upload' && !hideVipIcon"
      class="membership-badge membership-badge--expandable membership-badge--premium"
      aria-label="高级"
    >
      <img
        class="membership-badge__compact"
        :src="premiumCompactIcon"
        alt=""
        aria-hidden="true"
      />
      <img
        class="membership-badge__full"
        :src="premiumFullIcon"
        alt=""
        aria-hidden="true"
      />
    </span>

    <span v-if="duration" class="music-item__duration">{{ duration }}</span>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import AudioPlayer from '@/utils/AudioPlayer';
  import premiumCompactIcon from '@/assets/icons/svg/create-membership-premium.svg?url';
  import premiumFullIcon from '@/assets/icons/svg/create-membership-premium-full.svg?url';

  export type MusicItemVariant = 'no-music' | 'upload' | 'track';

  const props = defineProps<{
    variant: MusicItemVariant;
    name: string;
    duration?: string;
    selected?: boolean;
    /** 音频试听 URL（仅 track 类型需要） */
    audioUrl?: string;
    /** 是否隐藏高级会员角标（高级会员为 true） */
    hideVipIcon?: boolean;
  }>();

  defineEmits<{
    click: [];
  }>();

  // ============================================================
  // 试听播放逻辑（仅 track 类型）
  // ============================================================

  /** 当前正在播放的 URL（模块级共享，确保同一时间只有一个 MusicItem 处于播放态） */
  const playingUrl = ref<string | null>(null);

  const isPlaying = computed(() => {
    return props.audioUrl != null && playingUrl.value === props.audioUrl;
  });

  function handleIconClick(e: Event) {
    e.stopPropagation();
    if (!props.audioUrl) return;

    const player = AudioPlayer.instance;

    if (isPlaying.value) {
      player.stop();
      playingUrl.value = null;
    } else {
      // 重置播放状态的公共回调
      const resetPlaying = () => {
        playingUrl.value = null;
      };
      player.play(props.audioUrl, resetPlaying, resetPlaying);
      playingUrl.value = props.audioUrl;
    }
  }
</script>

<style scoped>
  .music-item {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 104px;
    padding: 0 18px;
    background: #fbf9ff;
    border: 1px solid transparent;
    border-radius: 5px;
    cursor: pointer;
    box-sizing: border-box;
    transition:
      border-color 0.2s,
      background-color 0.2s;
  }

  .music-item:hover {
    background: #f3f0ff;
  }

  .music-item--selected {
    border-color: #6033e1;
    background: #fbf9ff;
  }

  .music-item__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 66px;
    flex-shrink: 0;
  }

  .music-item__icon--no-music {
    background: transparent;
  }

  .music-item__icon--no-music .no-music-svg {
    width: 24px;
    height: 24px;
  }

  .music-item__icon--upload {
    background: transparent;
  }

  .membership-badge {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 4px;
    transition: width 0.22s ease;
  }

  .membership-badge__compact,
  .membership-badge__full {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    max-width: none;
    display: block;
    pointer-events: none;
    transition: opacity 0.18s ease;
  }

  .membership-badge__compact {
    width: 16px;
    opacity: 1;
  }

  .membership-badge__full {
    width: 39px;
    opacity: 0;
  }

  .music-item:hover .membership-badge--expandable {
    width: 39px;
  }

  .music-item:hover .membership-badge--expandable .membership-badge__compact {
    opacity: 0;
  }

  .music-item:hover .membership-badge--expandable .membership-badge__full {
    opacity: 1;
  }

  .music-item__icon--track {
    background: #ede8ff;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .music-item__icon--track:hover {
    background: #ddd6ff;
  }

  .music-item__icon--playing {
    background: #6033e1;
  }

  .music-item__icon--playing:hover {
    background: #7c5cec;
  }

  .music-item__name {
    flex: 1;
    font-size: 13px;
    font-weight: 350;
    color: #333333;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .music-item__duration {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    line-height: 17px;
    flex-shrink: 0;
  }

  .music-item__badge {
    display: inline-flex;
    align-items: center;
    height: 16px;
    padding: 0 5px;
    font-size: 10px;
    font-weight: 500;
    color: #ff6000;
    background: linear-gradient(135deg, #fff3e0, #fff8f0);
    border-radius: 3px;
    flex-shrink: 0;
  }
</style>
